import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import "../styles/vendor.css";

/**
 * BarcodeScanner — scanner inline réutilisable
 * Essaie : caméra arrière (exact) → caméra arrière (relaxed) → n'importe quelle caméra (desktop)
 *
 * Props :
 *   onScan(decodedText) — appelé une seule fois dès le premier scan réussi
 *   onClose()           — appelé quand l'utilisateur ferme le scanner
 */
const CONTAINER_ID = "vendor-barcode-scanner-view";

const CAMERA_CONFIGS = [
  { facingMode: { exact: "environment" } }, // mobile : caméra arrière stricte
  { facingMode: "environment" },             // mobile : caméra arrière relaxée
  {},                                        // desktop / fallback : n'importe quelle caméra
];

const SCAN_CONFIG = {
  fps: 10,
  qrbox: (w, h) => {
    const size = Math.min(w, h) * 0.95;
    return { width: size, height: size };
  },
};

const BarcodeScanner = ({ onScan, onClose }) => {
  const scannerRef = useRef(null);
  const stoppedRef = useRef(false);

  const [status, setStatus] = useState("starting"); // "starting" | "active" | "error"
  const [errorMsg, setErrorMsg] = useState("");

  const safeStop = () => {
    if (stoppedRef.current || !scannerRef.current) return;
    stoppedRef.current = true;
    try {
      scannerRef.current.stop()
        .then(() => {
          try { scannerRef.current?.clear(); } catch (_) {}
        })
        .catch(() => {
          try { scannerRef.current?.clear(); } catch (_) {}
        });
    } catch (_) {}
  };

  useEffect(() => {
    let mounted = true;

    const start = async () => {
      await new Promise((res) => setTimeout(res, 350));
      if (!mounted || !document.getElementById(CONTAINER_ID)) return;

      const html5QrCode = new Html5Qrcode(CONTAINER_ID);
      scannerRef.current = html5QrCode;

      for (const config of CAMERA_CONFIGS) {
        if (!mounted) return;
        try {
          await html5QrCode.start(
            config,
            SCAN_CONFIG,
            (decodedText) => {
              safeStop();
              if (mounted) onScan(decodedText);
            }
          );
          if (mounted) setStatus("active");
          return;
        } catch (err) {
          console.warn("[BarcodeScanner] Config échouée :", config, "→", err?.message);
        }
      }

      if (mounted) {
        setStatus("error");
        setErrorMsg("Caméra inaccessible. Utilisez la saisie manuelle.");
      }
    };

    start();

    // Traduit les labels caméra injectés par html5-qrcode
    const observer = new MutationObserver(() => {
      const container = document.getElementById(CONTAINER_ID);
      if (!container) return;
      const select = container.querySelector("select");
      if (select) {
        Array.from(select.options).forEach(opt => {
          const t = opt.text.toLowerCase();
          if (t.includes("back") || t.includes("environment") || t.includes("arrière")) {
            opt.text = "📷 Caméra arrière";
          } else if (t.includes("front") || t.includes("user") || t.includes("avant")) {
            opt.text = "🤳 Caméra avant";
          }
        });
      }
    });

    const container = document.getElementById(CONTAINER_ID);
    if (container) observer.observe(container, { childList: true, subtree: true, characterData: true });

    return () => {
      mounted = false;
      observer.disconnect();
      safeStop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{
      position: "relative",
      background: "#0d0d0d",
      borderRadius: "12px",
      overflow: "hidden",
      marginBottom: "16px",
    }}>
      {/* Titre */}
      <p style={{
        color: "#fff",
        fontSize: "11px",
        fontWeight: 800,
        letterSpacing: "0.12em",
        textTransform: "uppercase",
        textAlign: "center",
        margin: "14px 0 2px",
      }}>
        POSITIONNEZ LE CODE DANS LE CADRE
      </p>
      <p style={{
        color: "rgba(255,255,255,0.4)",
        fontSize: "9px",
        fontWeight: 600,
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        textAlign: "center",
        margin: "0 0 10px",
      }}>
        LE CODE DOIT ÊTRE BIEN VISIBLE ET CENTRÉ
      </p>

      {/* Zone caméra */}
      <div style={{ position: "relative" }}>
        <div id={CONTAINER_ID} style={{ width: "100%", minHeight: "320px", background: "#000" }} />

        {/* Coins dorés */}
        {status === "active" && (
          <div style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 10 }}>
            {[
              { top: 0, left: 0, borderWidth: "3px 0 0 3px", borderRadius: "2px 0 0 0" },
              { top: 0, right: 0, borderWidth: "3px 3px 0 0", borderRadius: "0 2px 0 0" },
              { bottom: 0, left: 0, borderWidth: "0 0 3px 3px", borderRadius: "0 0 0 2px" },
              { bottom: 0, right: 0, borderWidth: "0 3px 3px 0", borderRadius: "0 0 2px 0" },
            ].map((s, i) => (
              <span key={i} style={{
                position: "absolute",
                width: 22, height: 22,
                borderColor: "#f59e0b",
                borderStyle: "solid",
                ...s,
              }} />
            ))}
            {/* Réticule */}
            <span style={{
              position: "absolute", top: "50%", left: "50%",
              width: 28, height: 1,
              background: "rgba(245,158,11,0.35)",
              transform: "translate(-50%,-50%)",
            }} />
            <span style={{
              position: "absolute", top: "50%", left: "50%",
              width: 1, height: 28,
              background: "rgba(245,158,11,0.35)",
              transform: "translate(-50%,-50%)",
            }} />
          </div>
        )}

        {/* Overlay démarrage */}
        {status === "starting" && (
          <div style={{
            position: "absolute", inset: 0,
            display: "flex", alignItems: "center", justifyContent: "center",
            color: "rgba(255,255,255,0.7)", fontSize: "13px", fontWeight: 600,
            background: "rgba(0,0,0,0.6)", gap: "8px",
          }}>
            <span style={{ fontSize: "1.2rem" }}>📷</span> Démarrage caméra…
          </div>
        )}

        {/* Erreur */}
        {status === "error" && (
          <div style={{
            padding: "20px", textAlign: "center",
            color: "#fca5a5", fontSize: "13px", fontWeight: 600,
          }}>
            ⚠️ {errorMsg}
          </div>
        )}
      </div>

      {/* Bouton fermer */}
      <div style={{ padding: "12px 16px 16px", display: "flex", justifyContent: "center" }}>
        <button
          type="button"
          onClick={onClose}
          style={{
            background: "transparent",
            border: "1.5px solid #f59e0b",
            color: "#f59e0b",
            borderRadius: "20px",
            padding: "8px 28px",
            fontSize: "12px",
            fontWeight: 700,
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            cursor: "pointer",
          }}
        >
          ARRÊTER LE SCAN
        </button>
      </div>
    </div>
  );
};

export default BarcodeScanner;
